import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Loader2, Layers, ArrowRight, Store, Crown, ShoppingCart } from 'lucide-react';
import { DashboardLayout } from '../../app/layouts/DashboardLayout';
import { MEMBER_NAV } from '../../constants/nav';
import { fetchMyCourses, fetchCourseCatalog, formatPrice } from '../../features/courses/api/courses';
import type { Course } from '../../types';
import { COURSE } from '../../constants/testIds';

export default function MyCoursesPage() {
  const navigate = useNavigate();
  const [owned, setOwned] = useState<Course[]>([]);
  const [catalog, setCatalog] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([fetchMyCourses(), fetchCourseCatalog()])
      .then(([mine, etalase]) => {
        setOwned(mine);
        setCatalog(etalase);
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <DashboardLayout navItems={MEMBER_NAV} title="My Courses">
      <div className="max-w-6xl">
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" strokeWidth={1.5} />
          </div>
        ) : (
          <div className="space-y-12">
            {/* ===== Etalase (belum dimiliki) ===== */}
            <section>
              <div className="flex items-center gap-2 mb-1">
                <Store className="w-4 h-4 text-cyan-400" strokeWidth={1.5} />
                <p className="text-xs uppercase tracking-[0.2em] text-cyan-400">Etalase Course</p>
              </div>
              <h2 className="text-2xl sm:text-3xl tracking-tight font-medium text-white">Belum Dimiliki</h2>
              <p className="text-white/60 mt-1 text-sm mb-6">Materi yang bisa Anda beli atau buka aksesnya.</p>

              {catalog.length === 0 ? (
                <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-8 text-center text-white/50">
                  Tidak ada course baru untuk dijelajahi saat ini.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid={COURSE.catalogList}>
                  {catalog.map((c) => {
                    const isVip = c.access_type === 'vip';
                    return (
                      <div
                        key={c.id}
                        data-testid={COURSE.catalogCard}
                        className="bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden flex flex-col transition-colors hover:border-cyan-400/30"
                      >
                        <div className="h-36 bg-gradient-to-br from-cyan-400/20 to-transparent flex items-center justify-center overflow-hidden relative">
                          {c.thumbnail_url ? (
                            <img src={c.thumbnail_url} alt={c.title} className="w-full h-full object-cover" />
                          ) : (
                            <BookOpen className="w-10 h-10 text-cyan-400/60" strokeWidth={1.5} />
                          )}
                          {isVip && (
                            <span className="absolute top-3 left-3 flex items-center gap-1 text-[10px] uppercase tracking-wider text-amber-300 bg-amber-400/15 border border-amber-400/30 px-2 py-0.5 rounded-full">
                              <Crown className="w-3 h-3" strokeWidth={1.5} /> Khusus VIP
                            </span>
                          )}
                        </div>
                        <div className="p-5 flex flex-col flex-1">
                          <span className="text-[10px] uppercase tracking-[0.15em] text-cyan-400 mb-1.5">{c.category}</span>
                          <h3 className="text-base font-medium text-white mb-1">{c.title}</h3>
                          <p className="text-sm text-white/50 line-clamp-2 flex-1">{c.description}</p>
                          <div className="flex items-center gap-1.5 text-xs text-white/40 mt-3">
                            <Layers className="w-4 h-4" strokeWidth={1.5} /> {c.lesson_count ?? 0} lesson
                          </div>
                          <div className="flex items-center justify-between mt-4">
                            <span className="text-cyan-400 font-medium text-sm">
                              {isVip ? 'Benefit VIP' : formatPrice(c.price, c.currency)}
                            </span>
                            <button
                              data-testid={COURSE.catalogCta}
                              onClick={() => navigate(isVip ? '/member/vip' : `/courses/${c.slug}`)}
                              className="bg-white/5 text-white border border-white/10 font-medium px-4 py-2 rounded-lg transition-colors hover:bg-white/10 hover:text-cyan-400 text-sm flex items-center gap-1.5"
                            >
                              {isVip ? (
                                <><Crown className="w-4 h-4" strokeWidth={1.5} /> Gabung VIP</>
                              ) : (
                                <><ShoppingCart className="w-4 h-4" strokeWidth={1.5} /> Beli</>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            {/* ===== Sudah Dimiliki ===== */}
            <section className="border-t-2 border-white/20 pt-10">
              <div className="flex items-center gap-2 mb-1">
                <BookOpen className="w-4 h-4 text-emerald-400" strokeWidth={1.5} />
                <p className="text-xs uppercase tracking-[0.2em] text-emerald-400">Course Saya</p>
              </div>
              <h2 className="text-2xl sm:text-3xl tracking-tight font-medium text-white">Sudah Dimiliki</h2>
              <p className="text-white/60 mt-1 text-sm mb-6">Course yang sudah Anda miliki aksesnya.</p>

              {owned.length === 0 ? (
                <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-8 text-center">
                  <BookOpen className="w-10 h-10 text-white/30 mx-auto mb-4" strokeWidth={1.5} />
                  <p className="text-white/60">Anda belum memiliki course.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid={COURSE.myList}>
                  {owned.map((c) => (
                    <button
                      key={c.id}
                      data-testid={COURSE.myCard}
                      onClick={() => navigate(`/member/courses/${c.id}`)}
                      className="text-left bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden flex flex-col transition-colors hover:border-cyan-400/30 group"
                    >
                      <div className="h-36 bg-gradient-to-br from-emerald-400/20 to-transparent flex items-center justify-center overflow-hidden">
                        {c.thumbnail_url ? (
                          <img src={c.thumbnail_url} alt={c.title} className="w-full h-full object-cover" />
                        ) : (
                          <BookOpen className="w-10 h-10 text-emerald-400/60" strokeWidth={1.5} />
                        )}
                      </div>
                      <div className="p-5 flex flex-col flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-[10px] uppercase tracking-[0.15em] text-emerald-400 bg-emerald-400/10 border border-emerald-400/20 px-2 py-0.5 rounded-full">
                            {c.access_status || 'active'}
                          </span>
                        </div>
                        <h3 className="text-base font-medium text-white mb-1">{c.title}</h3>
                        <p className="text-sm text-white/50 line-clamp-2 flex-1">{c.description}</p>
                        <div className="flex items-center justify-between mt-4 text-sm text-cyan-400">
                          <span className="flex items-center gap-1.5 text-white/40">
                            <Layers className="w-4 h-4" strokeWidth={1.5} /> {c.lesson_count ?? 0} lesson
                          </span>
                          <span className="flex items-center gap-1 group-hover:gap-2 transition-all">
                            Buka <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                          </span>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
